/**
 * pt 明細モーダル（完了 / 処理中 セクション + Backlog 母票リンク）
 * MyPage の月間pt カードと IDチーム看板の bullet chart から気泡式に展開する共通モーダル。
 * 対象期間 / 対象者の切り出しは呼び出し側が行い、このコンポーネントは渡された案件行を
 * 完了 / 処理中 に分けて小計・合計とともに描画するだけに徹する。
 * 実績 0pt の子チケットは両セクションとも表示しない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン案件の実績pt 明細（完了 + 処理中）モーダル。Backlog 母票へのリンク付き
 * @graph-connects none
 */
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { formatNum } from '../combat-power/combat-power-utils.js';
import { BACKLOG_VIEW_BASE } from './shared.js';
import type { CaseRow } from './shared.js';

/** @graph-connects none */
export interface PtDetailModalProps {
  /**
   * 表示対象のデザイン案件（対象者 / 対象期間での絞り込みは呼び出し側の責務）。
   * 実績 0pt の行はこのコンポーネント側で落とすため、渡す側で除外しなくてよい
   */
  cases: CaseRow[];
  /** モーダルタイトル（呼び出し側が期間 / 対象者を込みで組み立てる） */
  title: string;
  /** 表の下に出す注記（省略時は非表示） */
  note?: string;
  /** モーダル表示状態 */
  open: boolean;
  /** クローズ要求コールバック */
  onClose: () => void;
}

/** @graph-connects none */
const TH_CLASS =
  'py-2 px-3 text-left text-[11px] font-semibold text-[var(--color-text-muted)] whitespace-nowrap';
/** @graph-connects none */
const TD_CLASS = 'py-2 px-3 text-sm text-[var(--color-text-primary)]';
/** @graph-connects none */
const SECTION_TITLE_CLASS = 'text-sm font-bold text-[var(--color-text-primary)]';

/** Backlog 母票へのリンク @graph-connects none */
function IssueLink({ issueKey }: { issueKey: string }) {
  return (
    <a
      href={`${BACKLOG_VIEW_BASE}${issueKey}`}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-xs text-[var(--color-accent-primary)] hover:underline whitespace-nowrap"
    >
      {issueKey}
    </a>
  );
}

/** 完了 / 処理中 セクションの案件テーブル @graph-connects none */
function CaseSection({
  title,
  rows,
  showOwner,
}: {
  title: string;
  rows: CaseRow[];
  showOwner: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section>
      <h4 className={SECTION_TITLE_CLASS}>{title}</h4>
      {rows.length > 0 ? (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className={TH_CLASS}>{t('members.quarterlyKpi.ptDetail.colIssue', '番号')}</th>
                <th className={`${TH_CLASS} w-full`}>
                  {t('members.quarterlyKpi.ptDetail.colSummary', '案件名')}
                </th>
                {showOwner && <th className={TH_CLASS}>{t('idtKpi.columns.owner', '担当')}</th>}
                <th className={TH_CLASS}>{t('idtKpi.columns.category', '種別')}</th>
                <th className={`${TH_CLASS} text-right`}>pt</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.childIssueKey}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                >
                  <td className={TD_CLASS}>
                    <IssueLink issueKey={row.issueKey} />
                  </td>
                  <td className={TD_CLASS}>{row.name}</td>
                  {showOwner && (
                    <td
                      className={`${TD_CLASS} text-xs text-[var(--color-text-secondary)] whitespace-nowrap`}
                    >
                      {row.owner}
                    </td>
                  )}
                  <td
                    className={`${TD_CLASS} text-xs text-[var(--color-text-secondary)] whitespace-nowrap`}
                  >
                    {row.category}
                  </td>
                  <td className={`${TD_CLASS} text-right font-medium whitespace-nowrap`}>
                    {formatNum(row.actual, 1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-2 text-sm text-[var(--color-text-muted)]">
          {t('members.quarterlyKpi.ptDetail.empty', '該当する案件がありません')}
        </p>
      )}
    </section>
  );
}

/** @graph-connects none */
export function PtDetailModal({ cases, title, note, open, onClose }: PtDetailModalProps) {
  const { t } = useTranslation();

  // 実績 0pt の子チケットは明細から落とす。合計・小計に寄与せず行数だけ嵩むため
  // (親票に紐づくだけで pt を持たない情報設計 / マスターデザインマージ子票が該当する)。
  const scored = cases.filter((c) => c.actual !== 0);
  const completed = scored.filter((c) => c.status === '完了');
  const inProgress = scored.filter((c) => c.status !== '完了');
  const completedTotal = completed.reduce((s, c) => s + c.actual, 0);
  const inProgressTotal = inProgress.reduce((s, c) => s + c.actual, 0);
  // 担当列は複数デザイナーが混在するとき（= チーム全体表示）だけ出す
  const showOwner = new Set(scored.map((c) => c.owner)).size > 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={t('members.quarterlyKpi.ptDetail.description', {
        value: formatNum(completedTotal + inProgressTotal, 1),
        defaultValue: `合計 ${formatNum(completedTotal + inProgressTotal, 1)}pt`,
      })}
      size="full"
      className="animate-bubble-in md:max-w-7xl"
    >
      <div className="space-y-6">
        <CaseSection
          title={t('members.quarterlyKpi.ptDetail.completedSection', {
            value: formatNum(completedTotal, 1),
            defaultValue: `完了 ${formatNum(completedTotal, 1)}pt`,
          })}
          rows={completed}
          showOwner={showOwner}
        />
        <CaseSection
          title={t('members.quarterlyKpi.ptDetail.wipSection', {
            value: formatNum(inProgressTotal, 1),
            defaultValue: `処理中 ${formatNum(inProgressTotal, 1)}pt`,
          })}
          rows={inProgress}
          showOwner={showOwner}
        />
        {note != null && <p className="text-xs text-[var(--color-text-muted)]">{note}</p>}
      </div>
    </Modal>
  );
}
